import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, Brain, Wrench, RotateCcw, TrendingUp, Zap, Target, Users, Star, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Award, Clock, CheckCircle, Activity, Stethoscope, MapPin, Timer, Waypoints, Scale, Sprout, Dumbbell, HeartPulse, Thermometer, Briefcase, Square, Battery } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";
import "@/App.css";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-50px" }, transition: { duration: 0.6 } };
const stagger = { initial: {}, whileInView: {}, viewport: { once: true }, transition: { staggerChildren: 0.12 } };
const fadeChild = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };

function HeroSection() {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1709315957145-a4bad1feef28?w=1920&h=1080&fit=crop&q=80" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-gradient" />
        {/* Heavy dark wash — dark mode only. In light mode the gradient above already provides contrast without turning the hero into a black box. */}
        <div className="absolute inset-0 bg-[#0f0f10]/50 hidden dark:block" />
        {/* Data grid overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: "linear-gradient(rgba(0,122,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,122,255,0.3) 1px, transparent 1px)", backgroundSize: "60px 60px"}} />
        <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,122,255,0.08) 2px, rgba(0,122,255,0.08) 4px)", backgroundSize: "100% 4px"}} />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-24 pb-16">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-6">FIT FUSION ANALYTICS</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white mb-6 leading-[1.05] uppercase">
            Are you reaching your <span className="text-gradient">true performance potential?</span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 mb-10 leading-relaxed max-w-xl">
            Your performance is the outcome.<br />
            Discover what&rsquo;s driving it&mdash;and what traditional performance testing misses.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/book">
              <Button className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none px-10 py-6 text-base font-bold tracking-wide group" data-testid="hero-cta-button">
                Get Started <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
      <div className="hero-bottom-fade absolute bottom-0 left-0 right-0 h-32 md:h-48" />
    </section>
  );
}

const processSteps = [
  { title: "Evaluate", desc: "Initial assessment, movement screening, data capture, and baseline metrics to understand exactly where you are.", detail: "We use EMG telemetry, 2D video analysis, and biomechanical assessment to capture objective data about how your body moves. This creates a comprehensive baseline that guides everything we do." },
  { title: "Decode", desc: "Identify inefficiencies, asymmetries, weak links, and compensation patterns hiding in your movement.", detail: "Our analysis reveals what the eye can't see. We identify muscle activation imbalances, movement compensations, and performance limiters that may be holding you back or reducing movement efficiency." },
  { title: "Build", desc: "Create a personalized training or development plan based on your specific findings and goals.", detail: "Every plan is individualized. We design targeted exercise programs, set clear milestones, and establish measurable outcomes so you know exactly what success looks like." },
  { title: "Retrain", desc: "Use guided sessions, video practice, coaching cues, and feedback to retrain your movement patterns.", detail: "Through in-office sessions, video-guided practice, and ongoing coaching, we systematically retrain your neuromuscular patterns. The app keeps your training consistent between visits." },
  { title: "Measure", desc: "Track progress, review outcomes, adjust the plan, and determine your next-step progression.", detail: "Regular check-ins and reassessments show measurable improvement. We compare your current data to baseline, celebrate wins, and adjust your program for continued progress." },
  { title: "Evaluate", desc: "Re-evaluate your progress and start the next climb.", detail: "Every cycle ends where it began: a fresh evaluation. This is how we measure the actual change in your body — not feelings, not guesswork. From here, we set the next goal and begin the climb again, smarter than before." },
];

// Each step sits on a peak or shoulder along the main ridge so the numbers
// follow the actual mountain shape (up-down-up) while trending overall upward.
const stepPositions = [
  { x: 8,  y: 78 },   // 1 Evaluate — base camp / first slope
  { x: 22, y: 56 },   // 2 Decode    — first peak
  { x: 37, y: 66 },   // 3 Build     — saddle dip
  { x: 54, y: 44 },   // 4 Retrain   — second, taller peak
  { x: 70, y: 54 },   // 5 Measure   — sub-peak
  { x: 90, y: 20 },   // 6 Evaluate  — summit
];

function BelowTheSurfaceSection() {
  // Editorial "surface vs depth" split. No literal iceberg imagery — the
  // metaphor is carried by:
  //   • the top card being muted (borders + muted-foreground text)
  //   • a shimmery waterline divider carrying the tagline
  //   • the bottom card lit with the brand blue (border, inner glow,
  //     data-grid overlay) so "below" reads as the moment Fit Fusion
  //     takes over
  // Layout collapses cleanly: two columns → one column on <md, and the
  // factors bar reflows 5 → 3 → 2 columns as the viewport shrinks.
  const surfaceSee = ["Speed", "Strength", "Power", "Endurance"];
  const surfaceMeasured = [
    { icon: Square,  label: "Force Plates" },
    { icon: Battery, label: "Load Cells" },
    { icon: MapPin,  label: "GPS" },
    { icon: Timer,   label: "Speed Testing" },
  ];
  const depthSee = [
    "Neuromuscular Coordination",
    "Hidden Performance-Limiting Factors",
    "Compensations",
    "Untapped Performance Potential",
  ];
  const depthMeasured = [
    { icon: Activity,  label: "Surface EMG" },
    { icon: Zap,       label: "Muscle Activity Analysis" },
    { icon: Waypoints, label: "Neuromuscular Pattern Analysis" },
    { icon: Scale,     label: "Symmetry & Force Transfer Analysis" },
  ];
  const factors = [
    { icon: Sprout,      label: "Growth" },
    { icon: Dumbbell,    label: "Training" },
    { icon: HeartPulse,  label: "Injury" },
    { icon: Thermometer, label: "Illness" },
    { icon: Briefcase,   label: "Repetitive Load" },
  ];

  return (
    <section id="below-the-surface" className="py-24 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto" data-testid="below-the-surface-section">
      {/* Intro */}
      <motion.div {...fadeUp} className="mb-10 md:mb-12 max-w-3xl">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-3">BELOW THE SURFACE</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold mb-6 uppercase leading-[1.1]">
          Why doesn&rsquo;t performance always reflect your <span className="text-gradient">true potential?</span>
        </h2>
        <p className="text-lg md:text-xl font-bold mb-4">
          Your body changes. <span className="text-muted-foreground font-normal">Your training usually doesn&rsquo;t.</span>
        </p>
        <p className="text-muted-foreground leading-relaxed">
          If the way your muscles work has changed and nobody recognizes it, you may be training on a system that no longer functions optimally&mdash;potentially limiting performance and increasing unnecessary overload.
        </p>
      </motion.div>

      {/* Iceberg hero — background image with typography-first overlays.
          The source image (1376×680, cropped) has its physical waterline at
          ~43% from the top. Because we use `background-size: cover` and our
          container aspect is always narrower than the image aspect, the
          waterline lands at ~43% of the container regardless of viewport.
          Padding is applied INSIDE each row (not on the grid parent) so grid
          rows measure against the full container height:
            - Row 1 (~39%): "Above The Waterline" — sky + iceberg tip
            - Row 2 (~8%):  Waterline tagline centered EXACTLY on the
              physical waterline (row center = 43%)
            - Row 3 (~53%): "Below The Waterline" — anchored to top so it
              sits directly under the waterline
          Min-heights are calibrated so the below content ends near the
          bottom of the image (no big empty pool of water underneath).
          ~72% dark-navy overlay keeps text legible while the iceberg still
          shows through as a subtle backdrop. */}
      <motion.div
        {...fadeUp}
        className="relative w-full overflow-hidden border border-border bg-black min-h-[960px] sm:min-h-[820px] md:min-h-[640px] lg:min-h-[640px]"
        style={{
          backgroundImage: "url(/images/iceberg-cross-section.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="iceberg-hero"
      >
        {/* Solid readability overlay — ~72% dark navy so text sits cleanly on the image */}
        <div
          className="absolute inset-0 pointer-events-none bg-[rgba(3,10,28,0.72)]"
          aria-hidden="true"
        />

        <div
          className="relative grid min-h-[960px] sm:min-h-[820px] md:min-h-[640px] lg:min-h-[640px]"
          style={{ gridTemplateRows: "39% 8% 53%" }}
        >
          {/* ABOVE THE WATERLINE — top-anchored, over the sky/iceberg tip */}
          <div className="self-start px-6 md:px-10 lg:px-14 pt-4 md:pt-5 lg:pt-6" data-testid="above-waterline-card">
            <div className="border-b border-white/30 pb-3 mb-5 md:mb-6">
              <p className="text-[11px] tracking-[0.28em] uppercase font-bold text-white">Above The Waterline</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-14">
              <div data-testid="above-see">
                <h3 className="text-xs tracking-[0.22em] uppercase font-bold text-white/95 mb-3 md:mb-4">What You Can See</h3>
                <ul className="grid grid-cols-2 md:grid-cols-1 gap-x-6 gap-y-2">
                  {surfaceSee.map((s) => (
                    <li key={s} className="flex items-center gap-3 text-sm md:text-base text-white">
                      <span className="w-4 h-px bg-white/85 shrink-0" aria-hidden="true" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div data-testid="above-measured">
                <h3 className="text-xs tracking-[0.22em] uppercase font-bold text-white/95 mb-3 md:mb-4">How It Is Traditionally Measured</h3>
                <ul className="grid grid-cols-2 md:grid-cols-1 gap-x-6 gap-y-2">
                  {surfaceMeasured.map(({ icon: Icon, label }) => (
                    <li key={label} className="flex items-center gap-3 text-sm md:text-base text-white">
                      <Icon className="w-4 h-4 shrink-0 text-white/90" strokeWidth={1.5} />
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Waterline tagline — row center sits at ~38% of container = physical waterline */}
          <div className="flex items-center gap-4 md:gap-6 self-center px-6 md:px-10 lg:px-14" data-testid="waterline-divider">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/70 to-[#00CC52]/50" />
            <p className="text-xs md:text-sm italic text-white tracking-wide whitespace-nowrap">
              Measure below the surface.
            </p>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent via-white/70 to-[#00CC52]/50" />
          </div>

          {/* BELOW THE WATERLINE — top-anchored, sits just under the waterline */}
          <div className="self-start w-full px-6 md:px-10 lg:px-14 pt-4 md:pt-6 pb-6 md:pb-10 lg:pb-14" data-testid="below-waterline-card">
            <div className="border-b border-[#4DA3FF]/50 pb-3 mb-5 md:mb-6">
              <p className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#7DBFFF]">Below The Waterline</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-14">
              <div data-testid="below-see">
                <h3 className="text-xs tracking-[0.22em] uppercase font-bold text-white/95 mb-3 md:mb-4">What You Can&rsquo;t See</h3>
                <ul className="space-y-2 md:space-y-2.5">
                  {depthSee.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-sm md:text-base text-white">
                      <span className="mt-[0.7em] w-4 h-px bg-[#7DBFFF] shrink-0" aria-hidden="true" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div data-testid="below-measured">
                <h3 className="text-xs tracking-[0.22em] uppercase font-bold text-white/95 mb-3 md:mb-4">How Fit Fusion Measures It</h3>
                <ul className="space-y-2 md:space-y-2.5">
                  {depthMeasured.map(({ icon: Icon, label }) => (
                    <li key={label} className="flex items-center gap-3 text-sm md:text-base text-white">
                      <Icon className="w-4 h-4 shrink-0 text-[#7DBFFF]" strokeWidth={1.75} />
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Factors bar */}
      <motion.div {...fadeUp} className="mt-14 md:mt-16 bg-card border border-border px-6 md:px-10 py-10" data-testid="factors-bar">
        <p className="text-center text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-muted-foreground mb-8">
          Many factors may influence how your body performs
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 md:gap-4 max-w-4xl mx-auto">
          {factors.map(({ icon: Icon, label }) => (
            <div key={label} className="flex flex-col items-center text-center gap-3">
              <span className="inline-flex w-14 h-14 items-center justify-center border border-[#007AFF]/40 text-[#007AFF]">
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </span>
              <span className="text-[11px] md:text-xs tracking-[0.15em] uppercase text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

function ProcessSection() {
  const [active, setActive] = useState(null);
  return (
    <section id="how-it-works" className="py-24 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto" data-testid="process-section">
      <motion.div {...fadeUp} className="mb-12">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-3">THE PROCESS</p>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-extrabold mb-4">How It Works</h2>
        <p className="text-muted-foreground max-w-2xl">Every program starts and ends with an evaluation. Between them: a deliberate climb. Tap a stage to see what happens at each point on the journey.</p>
      </motion.div>

      {/* Mountain journey graphic */}
      <motion.div {...fadeUp} className="relative bg-card border border-border" data-testid="journey-graphic">
        <div className="relative w-full aspect-[16/8] overflow-hidden">
          <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#007AFF" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#007AFF" stopOpacity="0.02" />
              </linearGradient>
              <linearGradient id="mountainFar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--muted-foreground))" stopOpacity="0.22" />
                <stop offset="100%" stopColor="hsl(var(--muted-foreground))" stopOpacity="0.04" />
              </linearGradient>
              <linearGradient id="mountainMid" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--muted-foreground))" stopOpacity="0.4" />
                <stop offset="100%" stopColor="hsl(var(--muted-foreground))" stopOpacity="0.1" />
              </linearGradient>
              <linearGradient id="mountainMain" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#007AFF" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#007AFF" stopOpacity="0.1" />
              </linearGradient>
              <linearGradient id="mountainNear" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0a1828" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#0a1828" stopOpacity="0.25" />
              </linearGradient>
            </defs>
            {/* Sky wash */}
            <rect x="0" y="0" width="100" height="50" fill="url(#sky)" />
            {/* Far background range — distant, hazy peaks */}
            <path d="M 0 38 L 6 32 L 14 36 L 22 28 L 30 34 L 40 26 L 50 32 L 58 24 L 68 30 L 78 22 L 86 28 L 94 20 L 100 25 L 100 50 L 0 50 Z" fill="url(#mountainFar)" />
            {/* Mid range — secondary silhouette */}
            <path d="M 0 44 L 8 36 L 16 40 L 26 32 L 36 38 L 46 30 L 56 36 L 66 28 L 76 32 L 86 22 L 96 26 L 100 22 L 100 50 L 0 50 Z" fill="url(#mountainMid)" />
            {/* Main climbing ridge — passes through every step (peaks + saddles) */}
            <path d="M 0 48 L 4 44 L 8 40 L 14 35 L 22 29 L 30 36 L 37 34 L 45 38 L 54 23 L 62 32 L 70 28 L 80 18 L 90 11 L 100 8 L 100 50 L 0 50 Z" fill="url(#mountainMain)" />
            {/* Foreground silhouettes — small dark ridges hugging the bottom edges for depth */}
            <path d="M 0 50 L 0 46 L 5 44 L 10 47 L 16 45 L 22 48 L 28 46 L 34 49 L 40 47 L 46 50 L 0 50 Z" fill="url(#mountainNear)" />
            <path d="M 60 50 L 64 47 L 70 49 L 76 46 L 82 48 L 88 45 L 94 49 L 100 47 L 100 50 L 60 50 Z" fill="url(#mountainNear)" />
            {/* Dotted climbing path connecting the stops along the ridge */}
            <path
              d={"M " + stepPositions.map(p => `${p.x} ${p.y * 0.5 + 1}`).join(" L ")}
              fill="none" stroke="#007AFF" strokeWidth="0.4" strokeDasharray="0.9 0.9" opacity="0.9"
            />
            {/* Summit flag */}
            <g transform={`translate(${stepPositions[5].x - 0.5}, ${stepPositions[5].y * 0.5 - 4})`}>
              <line x1="0" y1="0" x2="0" y2="4" stroke="#00CC52" strokeWidth="0.3" />
              <polygon points="0,0 2.5,0.7 0,1.4" fill="#00CC52" />
            </g>
          </svg>

          {/* Stop buttons positioned over the SVG */}
          {processSteps.map((step, i) => {
            const pos = stepPositions[i];
            const isActive = active === i;
            return (
              <button
                key={i}
                type="button"
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onClick={() => setActive(active === i ? null : i)}
                className={`absolute -translate-x-1/2 -translate-y-1/2 group focus:outline-none`}
                style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                data-testid={`journey-stop-${i + 1}`}
                aria-label={`${i + 1}. ${step.title}`}
              >
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-black text-sm md:text-base transition-all duration-300 border-2 ${isActive
                  ? "bg-[#007AFF] text-white border-[#007AFF] scale-110 shadow-lg shadow-[#007AFF]/40"
                  : "bg-background text-foreground border-[#007AFF]/40 group-hover:border-[#007AFF] group-hover:scale-110"}`}>
                  {i + 1}
                </div>
                <p className={`absolute left-1/2 -translate-x-1/2 mt-1.5 text-[10px] md:text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-colors ${isActive ? "text-[#007AFF]" : "text-muted-foreground group-hover:text-foreground"}`}>
                  {step.title}
                </p>
              </button>
            );
          })}

          {/* Detail popup overlay */}
          {active !== null && (
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md bg-background/95 backdrop-blur-sm border border-[#007AFF]/40 p-5 shadow-xl"
              onMouseLeave={() => setActive(null)}
              data-testid="journey-detail-popup"
            >
              <div className="flex items-baseline justify-between mb-2 gap-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#007AFF] text-white text-xs font-black flex items-center justify-center">{active + 1}</span>
                  <h3 className="text-lg font-black tracking-tight">{processSteps[active].title}</h3>
                </div>
                <button onClick={() => setActive(null)} className="text-muted-foreground hover:text-foreground text-xs" aria-label="Close" data-testid="journey-detail-close">✕</button>
              </div>
              <p className="text-sm text-muted-foreground mb-2">{processSteps[active].desc}</p>
              <p className="text-sm leading-relaxed">{processSteps[active].detail}</p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </section>
  );
}

const whoWeHelp = [
  { icon: Award,       label: "Elite / Competitive Athletes" },
  { icon: Users,       label: "Everyday Adults" },
  { icon: Sprout,      label: "Youth Athletes" },
  { icon: Stethoscope, label: "Healthcare Professionals" },
];

function PathwaysSection() {
  return (
    <section id="who-we-help" className="py-24 bg-secondary/30" data-testid="pathways-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div {...fadeUp} className="mb-10">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-3">WHO WE HELP</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-extrabold mb-6">Find Your Path</h2>
          <p className="text-muted-foreground max-w-2xl leading-relaxed">
            Whether you&rsquo;re chasing a podium, recovering from injury, building your athletic future, supporting the development of athletes, or making better clinical decisions &mdash; we have a proven approach for you.
          </p>
        </motion.div>

        <motion.div {...fadeUp}>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-foreground mb-5">Who Benefits</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {whoWeHelp.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-4 text-base md:text-lg text-foreground" data-testid={`who-we-help-${label.split(" ")[0].toLowerCase()}`}>
                <span className="inline-flex w-10 h-10 items-center justify-center border border-[#007AFF]/40 text-[#007AFF] shrink-0">
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                </span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}

const credentialsCards = [
  {
    key: "experience",
    value: "30+",
    label: "Years Experience",
    headline: "Clinical, elite-sport, academic — and a teacher of teachers",
    details: [
      "19 years embedded in European elite sport — traveling with national teams, on the floor at the biggest events on the calendar",
      "Parallel clinical practice in Germany across physiotherapy, sports physiotherapy, and osteopathic chiropractic",
      "Lecturer at the IFAO Institute for Applied Osteopathy, Germany since 2008 — teaching parietal osteopathy, cranio-sacral osteopathy, and applied osteopathy 40–60 days each year",
      "Continued academic study throughout — culminating in an M.Sc. in Osteopathy (2024)",
      "Founder of FIT FUSION ANALYTICS — translating that depth into objective, performance-only movement analysis in Raleigh, NC",
    ],
  },
  {
    key: "athletes",
    value: "Olympic",
    label: "Level Athletes",
    headline: "Trusted at the highest levels of sport",
    details: [
      "On staff supporting athletes at three Olympic Games",
      "Worked at multiple European Championships and World Championships across his German career",
      "Experience spans national champions, professional competitors, youth prospects, and recovering adults",
      "The same evaluation framework used with Olympians is applied — at the right scope — to every client",
    ],
  },
  {
    key: "methodology",
    value: "Data-Driven",
    label: "Methodology",
    headline: "What the eye misses, the data shows",
    details: [
      "EMG telemetry to measure how — and how much — each muscle is actually firing",
      "2D video motion analysis to expose movement patterns, asymmetries, and compensations",
      "Biomechanical screening rooted in functional anatomy, training theory, and applied research methodology",
      "Every program is built from baseline metrics and re-measured at the end — no guesswork, no symptoms-only fixes",
    ],
  },
  {
    key: "credentials",
    value: "Credentials",
    label: "Degrees & Training",
    headline: "European-trained. Academic, clinical, and accredited.",
    details: [
      "M.Sc. in Osteopathy — Hochschule Fresenius University of Applied Sciences, Germany (2024) · specializations in sports osteopathy & fasciae, paediatric osteopathy, and women's-health osteopathy",
      "Osteopathic Chiropractic — IFAO (Institut für Angewandte Osteopathie) · 5-year / 1,350-hour curriculum, completed 2007",
      "ACE Certified Medical Exercise Specialist — American Council on Exercise (US)",
      "Sports Physiotherapist — Deutscher Sportbund (German Sport Federation) · official elite-sport credential",
      "Additional specialist training: parietal, myofascial, visceral and cranio-sacral osteopathy, lymphatic drainage & edema therapy, and medical-massage therapy",
    ],
    note: "European clinical credentials inform the methodology; US services at FIT FUSION ANALYTICS are performance-based and do not constitute medical diagnosis or treatment.",
  },
];

function CredentialsSection() {
  const [active, setActive] = useState(null);
  const activeCard = active !== null ? credentialsCards[active] : null;
  return (
    <section id="about" className="py-24 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto" data-testid="credentials-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        <motion.div {...fadeUp} className="order-2 lg:order-1">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-3">MEET THE EXPERT</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-extrabold mb-6">Otto L. Thiel</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Movement-analysis and performance specialist, German-trained across physiotherapy, sports physiotherapy, and osteopathic chiropractic — with 19 years embedded in European elite sport, three Olympic Games on his résumé, and a longstanding role as a lecturer at one of Germany's leading osteopathy institutes.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Today, in Raleigh, NC, that experience powers an objective, data-first practice: measure how the body actually moves, find what's holding it back, and retrain it — for athletes, active adults, and developing youth alike.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-4" role="tablist" aria-label="Otto Thiel highlights">
            {credentialsCards.map((c, i) => {
              const isActive = active === i;
              return (
                <button
                  key={c.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="credentials-detail-panel"
                  onClick={() => setActive(isActive ? null : i)}
                  className={`text-left p-4 border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007AFF] ${isActive ? "bg-[#007AFF]/10 border-[#007AFF]" : "bg-card border-border hover:border-[#007AFF]/50"}`}
                  data-testid={`credential-card-${c.key}`}
                >
                  <p className={`text-xl font-black ${isActive ? "text-[#007AFF]" : "text-[#007AFF]"}`}>{c.value}</p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center justify-between gap-2">
                    <span>{c.label}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isActive ? "rotate-180 text-[#007AFF]" : "text-muted-foreground"}`} />
                  </p>
                </button>
              );
            })}
          </div>

          <p className="text-[11px] uppercase tracking-wider text-muted-foreground/70 mb-6">Tap a card to expand</p>

          {activeCard && (
            <motion.div
              id="credentials-detail-panel"
              key={activeCard.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="bg-card border border-[#007AFF]/40 p-6 mb-8"
              data-testid={`credential-detail-${activeCard.key}`}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="text-lg font-black tracking-tight">{activeCard.headline}</h3>
                <button
                  type="button"
                  onClick={() => setActive(null)}
                  className="text-muted-foreground hover:text-foreground text-xs leading-none"
                  aria-label="Close details"
                  data-testid="credential-detail-close"
                >
                  ✕
                </button>
              </div>
              <ul className="space-y-2.5">
                {activeCard.details.map((d, j) => (
                  <li key={j} className="flex gap-3 text-sm leading-relaxed">
                    <CheckCircle className="w-4 h-4 text-[#007AFF] mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{d}</span>
                  </li>
                ))}
              </ul>
              {activeCard.note && (
                <p className="mt-5 pt-4 border-t border-border text-xs leading-relaxed text-muted-foreground" data-testid="credential-detail-note">
                  <span className="font-bold text-[#007AFF]">Note:</span>{" "}
                  <span className="italic">{activeCard.note}</span>
                </p>
              )}
            </motion.div>
          )}

          <Link to="/book">
            <Button className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none px-8 py-5 font-bold tracking-wide" data-testid="about-cta">
              Get Started <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </motion.div>
        <motion.div {...fadeUp} className="order-1 lg:order-2 lg:sticky lg:top-24 lg:justify-self-end">
          <div className="w-full max-w-[340px] mx-auto lg:mx-0 space-y-3">
            <div className="relative">
              <img src="https://customer-assets.emergentagent.com/job_otto-retrain/artifacts/il2r2une_Otto-Thiel.webp" alt="Otto L. Thiel" className="w-full h-[420px] object-cover object-top" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0f0f10] to-transparent h-24" />
              <div className="absolute bottom-3 left-3 right-3">
                <p className="text-white text-sm font-bold">Founder, FIT FUSION ANALYTICS</p>
                <p className="text-gray-400 text-xs mt-1">Science-driven. Individualized. Measurable results.</p>
              </div>
            </div>

            {/* US-relevant certification — kept small and contained so it reads as
                a trust marker rather than a hero element. The card frame, eyebrow
                label, and muted-tone treatment match the rest of the site. */}
            <div className="bg-card border border-border p-3 flex items-center gap-3" data-testid="ace-certification-card">
              <img
                src="/ace-badge.png"
                alt="ACE Certified Medical Exercise Specialist"
                className="w-14 h-14 flex-shrink-0 object-contain"
              />
              <div className="min-w-0">
                <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#007AFF]">US Certification</p>
                <p className="text-xs font-bold leading-snug mt-0.5">ACE Certified</p>
                <p className="text-xs text-muted-foreground leading-snug">Medical Exercise Specialist</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function StoriesSection() {
  const [stories, setStories] = useState([]);
  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/stories")
      .then(({ data }) => setStories(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Reset detail toggle whenever the carousel moves.
  useEffect(() => { setExpanded(false); }, [index]);

  if (stories.length === 0) return null;

  const total = stories.length;
  const s = stories[index];
  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  return (
    <section id="stories" className="py-24 bg-secondary/30" data-testid="stories-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div {...fadeUp} className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-3">CLIENT STORIES</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-extrabold mb-4">Real People. Real Results.</h2>
            <p className="text-muted-foreground max-w-2xl">Every client journey starts with data and ends with measurable transformation.</p>
          </div>
          {/* Carousel controls — hidden when there's only one story. */}
          {total > 1 && (
            <div className="flex items-center gap-3" data-testid="stories-carousel-controls">
              <span className="text-xs text-muted-foreground font-mono tabular-nums" data-testid="stories-position">
                {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </span>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous story"
                className="w-10 h-10 border border-border hover:border-[#007AFF] hover:text-[#007AFF] transition-colors flex items-center justify-center"
                data-testid="stories-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next story"
                className="w-10 h-10 border border-border hover:border-[#007AFF] hover:text-[#007AFF] transition-colors flex items-center justify-center"
                data-testid="stories-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </motion.div>

        {/* Single-card carousel. AnimatePresence handles the slide between stories. */}
        <div className="relative">
          <motion.div
            key={s.id || index}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-card border border-border overflow-hidden"
            data-testid={`story-card-${index}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-0">
              {/* Portrait — sized to match Otto's "Meet the Expert" photo. */}
              <div className="relative bg-secondary/40">
                {s.avatar ? (
                  <img
                    src={s.avatar}
                    alt={s.name || ""}
                    className="w-full h-[300px] sm:h-[360px] lg:h-[420px] object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-[300px] sm:h-[360px] lg:h-[420px] flex items-center justify-center bg-[#007AFF]/5 border-r border-border">
                    <span className="text-[#007AFF] font-black text-7xl opacity-60">{(s.name || "?").charAt(0)}</span>
                  </div>
                )}
                {/* Subtle bottom gradient + name plate, matching the Otto card */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0f0f10] to-transparent h-24 lg:h-28 pointer-events-none" />
                <div className="absolute bottom-3 left-4 right-4">
                  {s.name && <p className="text-white text-sm font-bold leading-tight">{s.name}</p>}
                  {s.type && <p className="text-[#007AFF] text-[10px] font-bold uppercase tracking-wider mt-0.5">{s.type}</p>}
                </div>
              </div>

              {/* Quote + optional detail */}
              <div className="p-8 md:p-10 flex flex-col">
                <p className="text-lg md:text-xl italic leading-relaxed text-foreground mb-6">"{s.quote}"</p>

                {(s.challenge || s.revealed || s.changed || s.outcome) && (
                  <>
                    {expanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 mt-2 border-t border-border"
                        data-testid="story-detail-panel"
                      >
                        {s.challenge && (
                          <div>
                            <p className="text-xs font-bold text-[#007AFF] uppercase tracking-wider mb-1.5">Challenge</p>
                            <p className="text-sm text-muted-foreground leading-relaxed">{s.challenge}</p>
                          </div>
                        )}
                        {s.revealed && (
                          <div>
                            <p className="text-xs font-bold text-[#007AFF] uppercase tracking-wider mb-1.5">Assessment Revealed</p>
                            <p className="text-sm text-muted-foreground leading-relaxed">{s.revealed}</p>
                          </div>
                        )}
                        {s.changed && (
                          <div>
                            <p className="text-xs font-bold text-[#007AFF] uppercase tracking-wider mb-1.5">What Changed</p>
                            <p className="text-sm text-muted-foreground leading-relaxed">{s.changed}</p>
                          </div>
                        )}
                        {s.outcome && (
                          <div>
                            <p className="text-xs font-bold text-[#00CC52] uppercase tracking-wider mb-1.5">Outcome</p>
                            <p className="text-sm font-medium leading-relaxed">{s.outcome}</p>
                          </div>
                        )}
                      </motion.div>
                    )}
                    <button
                      type="button"
                      className="mt-auto pt-6 text-xs text-[#007AFF] font-bold tracking-wider uppercase hover:text-[#3395FF] transition-colors self-start"
                  onClick={() => setExpanded((e) => !e)}
                  data-testid="story-toggle-detail"
                >
                  {expanded ? "Show less" : "Read full story →"}
                </button>
              </>
            )}
              </div>
            </div>
          </motion.div>

          {/* Dots — useful when there are more than a couple stories. */}
          {total > 1 && (
            <div className="flex justify-center gap-2 mt-8" role="tablist" aria-label="Story navigation">
              {stories.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Go to story ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 transition-all duration-200 ${i === index ? "w-8 bg-[#007AFF]" : "w-4 bg-border hover:bg-muted-foreground"}`}
                  data-testid={`stories-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function PackagesSection() {
  const [packages, setPackages] = useState([]);
  useEffect(() => { api.get("/packages").then(({ data }) => setPackages(data.filter(p => p.active))).catch(() => {}); }, []);

  if (packages.length === 0) return null;

  return (
    <section id="packages" className="py-24 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto" data-testid="packages-section">
      <motion.div {...fadeUp} className="mb-16 text-center">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-3">PACKAGES</p>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-extrabold mb-4">Choose Your Level</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">Every package includes measurable outcomes, program milestones, and a clear path forward. Move up or down based on your progress.</p>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {packages.map((pkg, i) => {
          const isHighlight = pkg.highlight;
          const hasBadge = pkg.badge;
          const borderDash = pkg.border_style === "dashed";
          const ctaColor = pkg.cta_color || (isHighlight ? "#007AFF" : "");
          const badgeColor = pkg.badge_color || "#007AFF";
          const checkColor = pkg.cta_color || "#007AFF";
          return (
            <motion.div key={pkg.id} {...fadeChild}
              className={`relative bg-card border p-7 flex flex-col ${borderDash ? `border-dashed border-[${badgeColor}]/40` : ""} ${isHighlight ? "border-[#007AFF] ring-1 ring-[#007AFF]" : borderDash ? "" : "border-border"}`}
              style={borderDash && !isHighlight ? { borderColor: `${badgeColor}40`, borderStyle: "dashed" } : undefined}
              data-testid={`package-card-${i}`}>
              {isHighlight && <div className="absolute top-0 left-0 right-0 h-1 bg-[#007AFF]" />}
              {hasBadge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1" style={{ backgroundColor: badgeColor }}>{pkg.badge}</span>}
              <h3 className="text-xl font-bold tracking-tight">{pkg.name}</h3>
              <div className="mt-4 mb-2">
                <span className="text-4xl font-black">{pkg.price}</span>
                <span className="text-muted-foreground text-sm">{pkg.period}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-6">{pkg.best_for}</p>
              <div className="space-y-3 flex-1">
                {(pkg.features || []).map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: checkColor }} />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <Link to={`/book?package=${pkg.booking_id || pkg.name.toLowerCase()}`} className="mt-8">
                <Button className={`w-full rounded-none py-5 font-bold tracking-wide ${ctaColor ? "text-white" : isHighlight ? "bg-[#007AFF] hover:bg-[#3395FF] text-white" : "bg-secondary hover:bg-secondary/80"}`}
                  style={ctaColor ? { backgroundColor: ctaColor } : undefined}>
                  {pkg.cta_text || "Get Started"}
                </Button>
              </Link>
            </motion.div>
          );
        })}
      </motion.div>
      <motion.div {...fadeUp} className="mt-8 text-center">
        <p className="text-sm text-muted-foreground">All packages include defined start and end dates, mid-program check-ins, and measurable outcomes. Clients can move between tiers based on progress and goals.</p>
      </motion.div>
    </section>
  );
}

function GetStartedSection() {
  return (
    <section id="get-started" className="py-28 px-6 md:px-12 lg:px-24" data-testid="get-started-section">
      <div className="max-w-5xl mx-auto">
        <div className="relative overflow-hidden bg-card border border-border">
          {/* Subtle accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#007AFF] via-[#00CC52] to-[#007AFF]" />
          {/* Faint data-grid texture */}
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(0,122,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(0,122,255,0.4) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />

          <motion.div {...fadeUp} className="relative grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 p-10 md:p-16 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-3">READY WHEN YOU ARE</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold mb-5 leading-[1.1]">
                Pick a time that works for you.
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8 max-w-xl">
                Every program starts with a conversation. Tell Otto where you are, where you want to go, and book the slot that fits. Calendar syncing, confirmations, and reminders are handled automatically.
              </p>
              <ul className="space-y-2.5 mb-10">
                {[
                  "Direct booking — no back-and-forth emails",
                  "Choose the program length that matches your goals",
                  "Confirmation and calendar invite sent instantly",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#007AFF] mt-0.5 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4 items-center">
                <Link to="/book">
                  <Button className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none px-10 py-6 text-base font-bold tracking-wide group" data-testid="get-started-section-cta">
                    Get Started <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href="#how-it-works" className="text-sm font-bold tracking-wide text-muted-foreground hover:text-foreground transition-colors">
                  See how it works →
                </a>
              </div>

              {/* Group / multi-person awareness note */}
              <div className="mt-8 flex items-start gap-3 border-l-2 border-[#00CC52]/60 pl-4 py-1" data-testid="group-discount-note">
                <Users className="w-4 h-4 text-[#00CC52] mt-1 flex-shrink-0" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <span className="text-foreground font-semibold">Training with others?</span> Group sessions for teams, families, and training partners are available at a discount.{" "}
                  <Link to="/book" className="text-[#007AFF] hover:text-[#3395FF] font-semibold whitespace-nowrap" data-testid="group-discount-cta">
                    Ask Otto when you book →
                  </Link>
                </p>
              </div>
            </div>

            <div className="relative hidden lg:flex flex-col items-center justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#007AFF]/10 blur-2xl" />
                <div className="relative bg-background border border-[#007AFF]/40 px-10 py-12 text-center">
                  <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#007AFF] mb-2">STEP 01</p>
                  <p className="text-4xl font-black tracking-tight mb-1">Book.</p>
                  <p className="text-4xl font-black tracking-tight mb-1 text-muted-foreground/40">Move.</p>
                  <p className="text-4xl font-black tracking-tight text-muted-foreground/20">Measure.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function TechSection() {
  return (
    <section id="tech" className="py-24 bg-secondary/30" data-testid="tech-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-3">THE TECHNOLOGY</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-extrabold mb-6">Why Data Matters</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Subjective guesswork leads to generic programs. Objective measurement leads to targeted solutions. Our technology captures what the eye can't see.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Activity, title: "EMG Telemetry", desc: "Measure muscle activation patterns in real-time" },
                { icon: Target, title: "Motion Analysis", desc: "Track movement efficiency and biomechanics" },
                { icon: Zap, title: "2D Video Analysis", desc: "Frame-by-frame movement breakdown" },
                { icon: TrendingUp, title: "Progress Tracking", desc: "Quantified outcomes over time" },
              ].map((t) => (
                <div key={t.title} className="bg-card border border-border p-4">
                  <t.icon className="w-5 h-5 text-[#007AFF] mb-2" />
                  <h4 className="text-sm font-bold">{t.title}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{t.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            {/* Real EMG telemetry analysis screenshot from Otto's practice.
                Uses object-contain on a card background so the data overlay
                (lower-right) isn't cropped at any aspect ratio. */}
            <div className="bg-card border border-border p-4">
              <img
                src="/tech-emg.png"
                alt="EMG telemetry sensors on a client's legs with corresponding muscle-activation analysis from Otto's biomechanics platform"
                className="w-full h-[420px] object-contain"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Brand-recolored PNG icons (sourced from user-provided artwork, recolored to #007AFF).
// Using <img> so the original artwork is preserved exactly.
const SpineIcon = ({ className }) => (
  <img src="/icons/spine.png" alt="" aria-hidden="true" className={`${className} object-contain`} />
);

const HandsHeartIcon = ({ className }) => (
  <img src="/icons/hands.png" alt="" aria-hidden="true" className={`${className} object-contain`} />
);

const AsclepiusIcon = ({ className }) => (
  <img src="/icons/rod-of-asclepius.png" alt="" aria-hidden="true" className={`${className} object-contain`} />
);

function ReferralSection() {
  const partners = [
    { icon: SpineIcon, type: "Chiropractors", desc: "Spinal and joint alignment support" },
    { icon: HandsHeartIcon, type: "Physical Therapists", desc: "Post-surgical and injury rehabilitation" },
    { icon: Stethoscope, type: "Sports Medicine", desc: "Diagnostic and medical oversight" },
    { icon: AsclepiusIcon, type: "Osteopaths", desc: "Holistic musculoskeletal care" },
  ];
  return (
    <section id="referrals" className="py-24 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto" data-testid="referral-section">
      <motion.div {...fadeUp} className="mb-12">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-3">REFERRAL NETWORK</p>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-extrabold mb-4">Collaborative Care</h2>
        <p className="text-muted-foreground max-w-2xl">We work alongside trusted providers to ensure seamless, comprehensive care for every client.</p>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {partners.map((p) => (
          <motion.div key={p.type} {...fadeChild} className="bg-card border border-border p-6 text-center hover:border-[#007AFF]/30 transition-colors">
            <p.icon className="w-10 h-10 text-[#007AFF] mx-auto mb-3" />
            <h4 className="text-sm font-bold mb-1">{p.type}</h4>
            <p className="text-xs text-muted-foreground">{p.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0f0f10] text-white" data-testid="cta-section">
      <div className="max-w-3xl mx-auto text-center">
        <motion.div {...fadeUp}>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#007AFF] mb-4">READY?</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-black mb-6 uppercase leading-[1.1]">
            Ready to find your true performance?
          </h2>
          <p className="text-lg md:text-xl text-gray-300 font-semibold tracking-wide leading-relaxed mb-10 max-w-xl mx-auto">
            Measure <span className="text-[#007AFF]">&bull;</span> Understand <span className="text-[#007AFF]">&bull;</span> Improve.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book">
              <Button className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none px-10 py-6 text-base font-bold tracking-wide pulse-glow" data-testid="cta-book-button">
                Get Started <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <HeroSection />
      {/* Order is intentional — matches the navbar/footer link order:
          Meet the Expert → The Process → Who We Help → Ready When You Are
          → Client Stories → The Technology → Referral Network → CTA. */}
      <CredentialsSection />
      <BelowTheSurfaceSection />
      <ProcessSection />
      <PathwaysSection />
      <GetStartedSection />
      <StoriesSection />
      {/* Old <PackagesSection /> hidden — Otto now uses Calendly for pricing + booking.
          Kept in the codebase so it can be re-enabled later if business model changes. */}
      <TechSection />
      <ReferralSection />
      <CTASection />
      <Footer />
    </div>
  );
}
